import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import BottomFixedContainer from '@/components/molecule/BottomFixedContainer';
import Button from '@/components/atom/Button';
import LucideIcon from '@/components/atom/Icons/LucideIcon';
import { BUTTON_CONST } from '@/src/constants/buttons';
import MainContentTitle from '@/components/molecule/MainContentTitle';
import DetailSection from '@/components/organism/DetailSection';
import { DetailSctionRenderer } from '@/components/organism/DetailSection/renderer';
import { NAV_CONST } from '@/src/constants/nav';
import Table from '@/components/atom/Table';
import { useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import SectionCard from '@/components/molecule/SectionCard';
import { getRecyclingProductsByIdAPI } from '@/src/apis/rest/recyclingProducts';
import { recyclingProductOptionColumn } from '@/src/pages/RecyclingProducts/optionColumn';
import PATH from '@/src/routes/path';
import { OPTIONS_CONST } from '@/src/constants/options';

const BACK_PATH = PATH.RECYCLING_PRODUCT_LIST;

function RecyclingProductDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const gridRef = useRef<AgGridReact>(null);

  const { data: productData } = useQuery({
    queryKey: ['recycling-product', id],
    queryFn: () => getRecyclingProductsByIdAPI(Number(id)),
    enabled: !!id,
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH, { replace: true });
      },
    },
  });

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={() => navigate(-1)}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
          <Link to={PATH.RECYCLING_PRODUCT_EDIT.replace(':id', id || '')}>
            <Button className="w-40" buttonSize="medium">
              {BUTTON_CONST.editShort}
            </Button>
          </Link>
        </div>
      </BottomFixedContainer>
    );
  };

  return (
    <>
      <MainContentTitle
        breadcrumbList={[
          NAV_CONST.recyclingProductManagement,
          NAV_CONST.recyclingProductDetail,
        ]}
        pageTitle={NAV_CONST.recyclingProductDetail}
      />
      <DetailSection
        sectionTitle={
          <div className="flex w-full items-center justify-between">
            <span className="font-pretendardSemiBold text-b18sb">
              수거 품목 정보
            </span>
          </div>
        }
        data={productData}
        contents={[
          [
            {
              key: 'category',
              lable: '수거 품목 명',
              valueSetter: data => data?.category || '-',
            },
            {
              key: 'isExposed',
              lable: '노출 여부',
              valueSetter: data => (data?.isExposed ? 'true' : 'false'),
              render: DetailSctionRenderer.BADGE({
                options: [
                  {
                    label: OPTIONS_CONST.exposed,
                    key: 'true',
                    badgeColor: 'lightGreen',
                  },
                  {
                    label: OPTIONS_CONST.notExposed,
                    key: 'false',
                    badgeColor: 'lightGray',
                  },
                ],
              }),
            },
            {
              key: 'isEventProduct',
              lable: '이벤트 여부',
              valueSetter: data => (data?.isEventProduct ? 'true' : 'false'),
              render: DetailSctionRenderer.BADGE({
                options: [
                  {
                    label: OPTIONS_CONST.applied,
                    key: 'true',
                    badgeColor: 'lightGreen',
                  },
                  {
                    label: OPTIONS_CONST.notApplied,
                    key: 'false',
                    badgeColor: 'lightGray',
                  },
                ],
              }),
            },
          ],
          [
            {
              key: 'productMediaImage',
              lable: '품목 이미지',
              valueSetter: data => data?.productMedia.bufferFullUrl || '',
              render: DetailSctionRenderer.IMAGE({ width: 128, height: 128 }),
            },
            {
              key: 'createdAt',
              lable: '등록 일시',
              valueSetter: data => data?.createdAt || '',
              render: DetailSctionRenderer.DATE({
                format: 'YYYY-MM-DD HH:mm:ss',
              }),
            },
          ],
        ]}
      />
      <div className="mb-[100px] mt-[24px]">
        <SectionCard
          cardTitle="수거 품목 옵션 정보"
          contentProps={{
            className: 'flex flex-col gap-y-4',
          }}
        >
          <Table
            tableClassName="mt-4 border border-gray0"
            ref={gridRef}
            rowData={productData?.recyclingProductOptions || []}
            isPaginationDataMapping
            columnDefs={recyclingProductOptionColumn()}
            total={0}
            isPagination={false}
          />
        </SectionCard>
      </div>
      {renderBottomFixedContent()}
    </>
  );
}

export default RecyclingProductDetailPage;
